// Legacy permission-zone keys.
//
// The custom-permissions editor used to offer three zone keys that were never
// real zones: `cap_monitoring`, `cap_assessments` and `documents`. Schools
// ticked them in good faith, so their user docs still carry those keys. The
// editor now writes the REAL keys (`cap_groupMonitoring`, `assessmentData`)
// and drops the old ones on save.
//
// That means a stored permission can live under either key depending on when
// the user was last saved, so every read of a custom user's permissions has to
// accept both. `resolveZoneValue` is that single read path — use it instead of
// indexing `sections` directly.
export const LEGACY_ZONE_ALIASES = {
  cap_monitoring: 'cap_groupMonitoring',
  cap_assessments: 'assessmentData',
  documents: null, // no equivalent - document access follows the summary zone
};

// current key → the old key that used to mean the same thing
const CURRENT_TO_LEGACY = Object.entries(LEGACY_ZONE_ALIASES)
  .reduce((acc, [legacy, current]) => {
    if (current) acc[current] = legacy;
    return acc;
  }, {});

// Zones every signed-in staff member gets by default, school-wide, whatever
// their role or scope (firestore.rules canReadZone enforces the same baseline).
// A Custom user CAN be opted out of one — but only by an explicit 'none'. A
// missing key means "never configured" and keeps the default, so a user saved
// before a zone existed never silently loses it. Values describe what is lost,
// for the warning the permission editor shows.
export const SCHOOL_WIDE_DEFAULT_ZONES = {
  summary: 'the key-facts card for every student',
  notes: 'Staff Notes for every student',
};

/** Read a zone out of a custom user's `sections` map, honouring the legacy key
 *  it may still be stored under. Returns 'none' when neither key is present —
 *  except for the school-wide defaults, where an ABSENT key keeps the default
 *  and only an explicit 'none' switches it off. */
export function resolveZoneValue(sections, zoneKey) {
  const map = sections || {};
  const direct = map[zoneKey];
  if (direct) return direct;
  const legacyKey = CURRENT_TO_LEGACY[zoneKey];
  const legacy = legacyKey && map[legacyKey];
  if (legacy) return legacy;
  // Never configured + school-wide default → keep the default (matches rules).
  if (SCHOOL_WIDE_DEFAULT_ZONES[zoneKey] && !(zoneKey in map)) return 'view';
  return 'none';
}
